import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import TimeSlotCard from './TimeSlotCard';

const DayScheduleSection = ({ 
  dayName, 
  dayData, 
  onUpdate, 
  onAddSlot, 
  onDeleteSlot,
  disabled = false 
}) => {
  const handleSlotUpdate = (slotIndex, field, value) => {
    onUpdate(dayName, slotIndex, field, value);
  };

  const handleAddSlot = () => {
    onAddSlot(dayName);
  };

  const handleDeleteSlot = (slotIndex) => {
    onDeleteSlot(dayName, slotIndex);
  };

  const validateSlots = (slots) => {
    const errors = [];
    const activeSlots = slots.filter(slot => slot.isActive);
    
    if (activeSlots.length > 1) {
      // Sort by start time
      const sortedSlots = [...activeSlots].sort((a, b) => 
        a.startTime.localeCompare(b.startTime)
      );
      
      for (let i = 0; i < sortedSlots.length - 1; i++) {
        const current = sortedSlots[i];
        const next = sortedSlots[i + 1];
        
        if (current.endTime > next.startTime) {
          errors.push(`Slots overlap: ${current.startTime}-${current.endTime} and ${next.startTime}-${next.endTime}`);
        }
      }
    }
    
    return errors;
  };

  const validationErrors = validateSlots(dayData.slots);

  return (
    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
            {dayName}
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label={`${dayData.totalDayHours} hours`} 
            color="primary" 
            variant="outlined"
            size="small"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddSlot}
            disabled={disabled}
          >
            Add Time Slot
          </Button>
        </Box>
      </Box>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">Time Slot Conflicts:</Typography>
          {validationErrors.map((error, index) => (
            <Typography key={index} variant="body2">• {error}</Typography>
          ))}
        </Alert>
      )}

      {/* Time Slots */}
      {dayData.slots.map((slot, index) => (
        <TimeSlotCard
          key={slot.id}
          slot={slot}
          slotIndex={index}
          dayName={dayName}
          onUpdate={handleSlotUpdate}
          onDelete={handleDeleteSlot}
          disabled={disabled}
        />
      ))}

      {/* Empty State */}
      {dayData.slots.length === 0 && (
        <Box textAlign="center" py={3} color="text.secondary">
          <ScheduleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2" gutterBottom>
            No time slots added for {dayName}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={handleAddSlot}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            Add First Time Slot
          </Button>
        </Box>
      )}

      {/* Summary */}
      {dayData.slots.length > 0 && (
        <Box mt={2} p={1} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="caption" color="text.secondary">
            {dayData.slots.length} slot(s) • {dayData.totalDayHours} total hours • 
            {dayData.slots.filter(s => s.isActive).length} active
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DayScheduleSection;
